/* eslint-disable max-lines */

import { deleteURLParams, setURLParams, showErrorAlert } from 'actions';
import { setTimeRange } from 'actions/dashboard';
import DateRangePicker from 'components/Calendar';
import { OPTIONS } from 'components/Dashboard/Filters/TimeRangeFilterRedesign';
import SelectRedesign from 'components/Select/SelectRedesign';
import DashboardFiltersTreeContext from 'contexts/DashboardFiltersTreeContext';
import moment from 'moment-timezone';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { DateRange, TZDate } from 'react-day-picker';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getSyncDates, getUser } from 'selectors/auth';
import { getDashboardState } from 'selectors/dashboard';
import { getNavParams } from 'selectors/nav';
import { useSelector } from 'store';
import logEvent from 'utils/analytics';
import { isValidDate } from 'utils/dateValidation';
import {
  TIME_RANGE_FIELD,
  UNIT_OF_TIME,
  days,
  getDateFilterStorageKey,
  halfYearToDate,
  hours,
  persistDateFilter,
  toISOWithoutMS,
  toTimeRange,
  useTimeRange,
  yearToDate,
} from 'utils/timeRanges';

export const dateOptions: {
  label: string;
  value: string;
  className?: string;
}[] = [
  { label: 'Date Range', value: 'date-range' },
  { label: 'Before', value: 'before' },
  { label: 'After', value: 'after', className: 'border-b border-litlingo-gray-2' },
];

export const timeRangeOptions: {
  label: string;
  value: string;
  className?: string;
}[] = [
  { label: 'Past 24 Hours', value: hours(-24) },
  { label: 'Past 7 Days', value: days(-7) },
  { label: 'Past 30 Days', value: days(-30) },
  { label: 'Past 60 Days', value: days(-60) },
  { label: 'Past 90 Days', value: days(-90) },
  { label: 'Past 6 Months', value: halfYearToDate() },
  { label: 'Year to Date', value: yearToDate() },
  { label: 'All Time', value: 'none' },
];

const DashboardDateFilter: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  const { activeUuid } = useParams<{ activeUuid: string }>();
  const dashboardState = useSelector(getDashboardState(activeUuid));
  const syncDates = useSelector(getSyncDates);

  const { uuid: userId = '', customer: { uuid: workspaceId = '' } = {} } = useSelector(getUser);
  const { handleAddValueToTree, handleRemoveFieldFromTree } = useContext(
    DashboardFiltersTreeContext
  );

  const [showCalendar, setShowCalendar] = useState(false);

  const urlRange = useTimeRange();
  const timeRange = useSelector((state) => getNavParams(state)[TIME_RANGE_FIELD] || '');

  useEffect(
    () => {
      if (!workspaceId || !userId) return;

      const saved = localStorage.getItem(getDateFilterStorageKey(workspaceId, userId));

      dispatch(
        setURLParams({
          [TIME_RANGE_FIELD]: saved ?? 'none',
        })
      );

      if (saved) {
        if (saved.includes(';')) {
          handleAddValueToTree(saved.replace(';', '<>'), 'date_range', '', true);
        } else if (saved === 'none') {
          handleRemoveFieldFromTree('date_range');
        } else {
          const [start, end] = toTimeRange(saved as string, true);
          const treeValue = `${moment(toISOWithoutMS(start)).utc().format()}<>${moment(
            toISOWithoutMS(end)
          )
            .utc()
            .format()}`;

          handleAddValueToTree(treeValue, 'date_range', '', true);
        }
      } else {
        handleRemoveFieldFromTree('date_range');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceId, userId]
  );

  const after =
    urlRange.created_after && !moment(urlRange.created_after).isSame(moment(new Date(0)))
      ? urlRange.created_after
      : '';
  const before = urlRange.created_before || '';

  let fromValue = '';
  let toValue = '';

  moment.tz.setDefault(dashboardState.primary_timezone);

  const createdAfterTZDate = isValidDate(after)
    ? new TZDate(moment(after).format(), dashboardState.primary_timezone)
    : undefined;
  const createdBeforeTZDate = isValidDate(before)
    ? new TZDate(moment(before).format(), dashboardState.primary_timezone)
    : undefined;

  if (createdAfterTZDate) {
    fromValue = moment(createdAfterTZDate).format('MM/DD/yyyy');
  }

  if (createdBeforeTZDate) {
    toValue = moment(createdBeforeTZDate).format('MM/DD/yyyy');
  }

  const [fromInput, setFromInput] = useState(fromValue);
  const [toInput, setToInput] = useState(toValue);

  const [dateOption, setDateOption] = useState<string>('date-range');

  const currentRange = [...OPTIONS, { label: 'Past 6 Months', value: '-6months' }].find(
    ({ value }) => value === timeRange
  );

  useEffect(() => {
    if (fromValue && !toValue) {
      setDateOption('after');
    }
    if (!fromValue && toValue) {
      setDateOption('before');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fromValue) {
      setFromInput(fromValue);
    }
    if (toValue) {
      setToInput(toValue);
    }
  }, [fromValue, toValue]);

  useEffect(() => {
    if (currentRange?.value) {
      setDateOption(currentRange?.value);
    }
  }, [currentRange?.value]);

  const getDateRangeOptions = (): {
    label: string;
    value: string;
    className?: string;
  }[] => {
    const fullDateOptions = [...dateOptions, ...timeRangeOptions];
    if (syncDates[0] && syncDates[1]) {
      fullDateOptions.push({ label: 'Investigation Period', value: 'sync-range' });
    }
    return fullDateOptions;
  };

  const handleSetDateRange = (range: DateRange): void => {
    const { from, to } = range;

    const fromUrl = from ? moment(from).startOf('day').utc().format() : null;
    const toUrl = to ? moment(to).endOf('day').utc().format() : null;

    let value = '';
    if (fromUrl && toUrl) {
      value = `${fromUrl};${toUrl}`;
    } else if (fromUrl) {
      value = `${fromUrl};`;
    } else {
      value = `;${toUrl}`;
    }

    dispatch(setURLParams({ [TIME_RANGE_FIELD]: value }));
    handleAddValueToTree(value.replace(';', '<>'), 'date_range', '', true);

    dispatch(setTimeRange({ after: fromUrl as string, before: toUrl as string }));

    persistDateFilter(workspaceId, userId, value);
  };

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setFromInput(value);

    const fromDate = moment(value, 'MM/DD/YYYY', true);

    if (createdBeforeTZDate && moment(createdBeforeTZDate).isValid()) {
      const beforeDate = moment(createdBeforeTZDate);
      if (fromDate.isAfter(beforeDate)) {
        dispatch(showErrorAlert('Invalid Date Range'));
        return;
      }
    }

    if (fromDate.isValid()) {
      handleSetDateRange({
        from: fromDate.toDate(),
        to: moment(toValue, 'MM/DD/YYYY', true).toDate(),
      });
    } else if (value.length > 9) {
      dispatch(showErrorAlert('Invalid Date'));
    } else if (value.length === 0) {
      handleSetDateRange({ from: undefined, to: moment(toValue, 'MM/DD/YYYY', true).toDate() });
    }
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setToInput(value);

    const toDate = moment(value, 'MM/DD/YYYY', true);

    if (createdAfterTZDate && moment(createdAfterTZDate).isValid()) {
      const afterDate = moment(createdAfterTZDate);
      if (toDate.isBefore(afterDate)) {
        dispatch(showErrorAlert('Invalid Date Range'));
        return;
      }
    }

    if (toDate.isValid()) {
      handleSetDateRange({
        from: moment(fromValue, 'MM/DD/YYYY', true).toDate(),
        to: toDate.toDate(),
      });
    } else if (value.length > 9) {
      dispatch(showErrorAlert('Invalid Date'));
    } else if (value.length === 0) {
      handleSetDateRange({ from: moment(fromValue, 'MM/DD/YYYY', true).toDate(), to: undefined });
    }
  };

  const defaultSelected: DateRange = {
    from: createdAfterTZDate,
    to: createdBeforeTZDate,
  };

  const handleDateOptionChanged = (value: string): void => {
    const prevOption = dateOption;
    setDateOption(value);

    if (value === 'after') {
      const rangeValue = prevOption === 'before' ? `${before};` : `${after};`;

      dispatch(setURLParams({ [TIME_RANGE_FIELD]: rangeValue }));
      handleAddValueToTree(rangeValue.replace(';', '<>'), 'date_range', '', true);

      dispatch(
        setTimeRange({
          after: prevOption === 'before' ? before : after,
          before: '',
        })
      );
      persistDateFilter(workspaceId, userId, rangeValue);
      return;
    }

    if (value === 'before') {
      const rangeValue = prevOption === 'after' ? `;${after}` : `;${before}`;

      dispatch(setURLParams({ [TIME_RANGE_FIELD]: rangeValue }));
      handleAddValueToTree(rangeValue.replace(';', '<>'), 'date_range', '', true);
      dispatch(
        setTimeRange({
          after: '',
          before: prevOption === 'after' ? after : before,
        })
      );
      persistDateFilter(workspaceId, userId, rangeValue);
      return;
    }

    if (value === 'sync-range') {
      logEvent(`envelopes-date-filter-${value}`);
      const [syncStart, syncEnd] = syncDates;
      if (!syncStart || !syncEnd) return;

      const rangeValue = `${moment(syncStart).utc().format()};${moment(syncEnd).utc().format()}`;

      dispatch(setURLParams({ [TIME_RANGE_FIELD]: rangeValue }));
      handleAddValueToTree(rangeValue.replace(';', '<>'), 'date_range', '', true);

      dispatch(
        setTimeRange({
          after: moment(syncStart).utc().format(),
          before: moment(syncEnd).utc().format(),
        })
      );
      persistDateFilter(workspaceId, userId, rangeValue);
      return;
    }

    if (value !== 'date-range') {
      logEvent(`dashboard-date-filter-${value}`);
      dispatch(setURLParams({ [TIME_RANGE_FIELD]: value }));
      dispatch(deleteURLParams([UNIT_OF_TIME]));

      const [start, end] = toTimeRange(value as string, true);
      const treeValue = `${moment(toISOWithoutMS(start)).utc().format()}<>${moment(
        toISOWithoutMS(end)
      )
        .utc()
        .format()}`;
      if (value === 'none') {
        handleRemoveFieldFromTree('date_range');
      } else {
        handleAddValueToTree(treeValue, 'date_range', '', true);
      }

      persistDateFilter(workspaceId, userId, value);
    }
  };

  return (
    <div
      aria-hidden
      ref={ref}
      className="flex flex-col gap-2"
      onClick={(): void => setShowCalendar(true)}
    >
      <div className="bg-litlingo-success-light bg-opacity-10 rounded">
        <SelectRedesign
          className="bg-transparent border-none"
          valueClassName="text-white px-0"
          optionClassname="text-litlingo-gray-6"
          onChange={(value): void => {
            handleDateOptionChanged(value);
          }}
          options={getDateRangeOptions()}
          value={dateOption}
        />
      </div>
      {dateOption === 'after' || dateOption === 'before' || dateOption === 'date-range' ? (
        <div className="w-full h-8 flex flex-row justify-start gap-1 py-1.5 text-body px-2 bg-litlingo-success-light bg-opacity-10 rounded">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.5 2.75C5.5 2.33579 5.16421 2 4.75 2C4.33579 2 4 2.33579 4 2.75V4H3C1.89543 4 1 4.89543 1 6V17C1 18.1046 1.89543 19 3 19H17C18.1046 19 19 18.1046 19 17V6C19 4.89543 18.1046 4 17 4H16.5V2.75C16.5 2.33579 16.1642 2 15.75 2C15.3358 2 15 2.33579 15 2.75V4H5.5V2.75ZM17.5 8H2.5V17C2.5 17.2761 2.72386 17.5 3 17.5H17C17.2761 17.5 17.5 17.2761 17.5 17V8Z"
              fill="white"
            />
          </svg>

          <div className="w-full flex flex-row gap-1">
            {dateOption !== 'before' && (
              <input
                data-testid="date-filter-input"
                type="text"
                placeholder={moment().format('MM/DD/yyyy')}
                className="text-white bg-transparent placeholder-litlingo-gray-3 w-19"
                value={fromInput}
                onChange={handleFromChange}
              />
            )}
            {dateOption !== 'after' && dateOption !== 'before' && (
              <span className="text-white">-</span>
            )}
            {dateOption !== 'after' && (
              <input
                data-testid="date-filter-input"
                type="text"
                placeholder={moment().format('MM/DD/yyyy')}
                className="text-white bg-transparent placeholder-litlingo-gray-3 w-19"
                value={toInput}
                onChange={handleToChange}
              />
            )}
          </div>
        </div>
      ) : null}
      {showCalendar && (
        <DateRangePicker
          containerRef={ref}
          defaultRange={defaultSelected}
          setShowCalendar={(): void => setShowCalendar(!showCalendar)}
          setDateRange={handleSetDateRange}
          dateOption={dateOption}
          timeZone={dashboardState.primary_timezone}
        />
      )}
    </div>
  );
};

export default DashboardDateFilter;
